import {
  Box,
  Button,
  Dialog,
  Backdrop,
  TextField,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { FormattedMessage } from 'react-intl';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

type CreateGibTypeDialogProps = {
  open: boolean;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const CreateGibTypeDialog = ({ open, handleClose: closeDialog, refresh }: CreateGibTypeDialogProps) => {
  // other hooks
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [title, setTitle] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  // async helper functions
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!title) {
      setError(true);
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.createGibType(title);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="gibTypes.createGibTypeCompleted"
            defaultMessage="GİB Yöntemi oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setTitle('');
    setError(false);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTitle(event.target.value);

  // useEffect hooks
  useEffect(() => {
    if (title) {
      setError(false);
    }
  }, [title]);

  return (
    <Dialog disableRestoreFocus open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="gibTypes.createGibType" defaultMessage="GİB Yöntemi Oluştur" />
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="gibTypes.createGibTypeText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir GİB Yöntemi oluşturabilirsiniz."
            />
          </DialogContentText>
          <TextField
            label={<FormattedMessage id="gibTypes.title" defaultMessage="Adı" />}
            fullWidth
            autoFocus
            size="small"
            onChange={onChangeHandler}
            value={title}
            error={error}
            helperText={
              error && <FormattedMessage id="validations.required" defaultMessage="Bu alan boş bırakılamaz" />
            }
          />
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default CreateGibTypeDialog;
